import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    HiOutlineInformationCircle,
    HiOutlinePencilAlt,
} from 'react-icons/hi'
import { Button, Tooltip } from 'components/ui'
import AvatarProfile from 'components/ui/Avatar/AvatarProfile'
import { USER_SYS } from 'constants/app.constant'
import { priorityStatusColor } from 'utils/storeHube/statusColor'
import { dateFormat, cleanIsoDate } from 'utils/storeHube/helpersDate'
import { withBotAvatar } from 'utils/storeHube/helpers'
import { StatusDropdown } from './TicketHeaderActions'
import AsideCard from './AsideCard'

const Field = ({ label, children, align = 'center' }) => (
    <div className={`flex gap-3 ${align === 'start' ? 'items-start' : 'items-center'}`}>
        <span className={`w-[92px] shrink-0 text-[12px] text-gray-500 dark:text-gray-400 ${align === 'start' ? 'pt-1' : ''}`}>
            {label}
        </span>
        <div className="flex-1 min-w-0 text-[13px] text-gray-800 dark:text-gray-100">
            {children}
        </div>
    </div>
)

const SectionDivider = () => (
    <div className="border-t border-dashed border-gray-200 dark:border-gray-700 my-1" />
)

const TicketDetailsCard = ({
    data,
    onChangeOperator,
    canUpdate,
    loadingUsersAllowed,
    nextStatus,
    canChangeStatus,
    onChangeStatus,
    loadingStatus,
}) => {
    const { t } = useTranslation()
    const [copied, setCopied] = useState(false)

    const handleCopyLink = () => {
        navigator.clipboard.writeText(window.location.href)
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
    }

    const {
        id,
        expired_at,
        external_code,
        created_at,
        props,
    } = data
    const operator = props?.operator
    const owner = props?.owner
    const ticket_category_primary = props?.ticket_category_primary
    const ticket_status = props?.ticket_status

    const priorityMeta = ticket_category_primary
        ? priorityStatusColor[ticket_category_primary?.priority ?? 0]
        : null

    return (
        <AsideCard
            icon={<HiOutlineInformationCircle className="w-4 h-4 text-gray-500 dark:text-gray-400" />}
            title={t('details', 'Detalles')}
            defaultExpanded
        >
            <div className="flex flex-col gap-3">
                <Field label={t('ticket_alias', 'Hallazgo')}>
                    <Tooltip title={copied ? t('copied', 'Copiado') : t('copy_link', 'Copiar Link')}>
                        <button
                            type="button"
                            onClick={handleCopyLink}
                            className={`font-semibold transition-colors cursor-pointer ${
                                copied
                                    ? 'text-emerald-600 dark:text-emerald-400'
                                    : 'text-gray-900 dark:text-gray-100 hover:text-indigo-600 dark:hover:text-indigo-400'
                            }`}
                        >
                            #{id}
                        </button>
                    </Tooltip>
                </Field>

                <Field label={t('status', 'Estado')}>
                    <StatusDropdown
                        statusId={ticket_status?.id}
                        nextStatus={nextStatus}
                        canChangeStatus={canChangeStatus}
                        loading={loadingStatus}
                        onChangeStatus={onChangeStatus}
                        t={t}
                    />
                </Field>

                <Field label={t('assigned_to', 'Asignado a')}>
                    {/* Mismo estilo que el chip de asignacion del TicketMetaStrip
                        (debajo del titulo): button con border + bg-gray-50,
                        lapiz visible siempre (no solo hover). Consistencia visual
                        entre los 2 puntos donde se ve el asignado. */}
                    <Tooltip
                        wrapperClass="flex w-full min-w-0"
                        title={
                            operator
                                ? [operator.name, operator.lastname]
                                      .filter(Boolean)
                                      .join(' ')
                                : undefined
                        }
                    >
                        <button
                            type="button"
                            onClick={canUpdate ? () => onChangeOperator?.() : undefined}
                            disabled={!canUpdate || loadingUsersAllowed}
                            className={[
                                'group flex min-w-0 max-w-full items-center justify-start text-left gap-1.5 px-1.5 py-1 rounded-md transition-colors',
                                canUpdate
                                    ? 'border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer'
                                    : 'cursor-default',
                            ].join(' ')}
                        >
                            {operator ? (
                                <>
                                    <AvatarProfile user={operator} size={18} className="shrink-0" />
                                    <span className="min-w-0 truncate font-semibold text-[13px] text-gray-900 dark:text-gray-100 leading-none">
                                        {operator.name} {operator.lastname}
                                    </span>
                                </>
                            ) : (
                                <span className="text-gray-500 dark:text-gray-400 italic leading-none">
                                    {t('not_assigned', 'Sin asignar')}
                                </span>
                            )}
                            {canUpdate && (
                                <HiOutlinePencilAlt
                                    className="w-3.5 h-3.5 shrink-0 text-gray-400 dark:text-gray-500 opacity-60 group-hover:opacity-100 transition-opacity ml-0.5"
                                    aria-hidden="true"
                                />
                            )}
                        </button>
                    </Tooltip>
                </Field>

                <Field label={t('priority', 'Prioridad')}>
                    {priorityMeta && ticket_category_primary?.priority ? (
                        <span className="inline-flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${priorityMeta.dotClass}`} />
                            <span className="text-[13px] font-medium text-gray-800 dark:text-gray-100">
                                {priorityMeta.label}
                            </span>
                        </span>
                    ) : (
                        <span className="text-gray-400 dark:text-gray-500">
                            {t('no-priority', 'Sin prioridad')}
                        </span>
                    )}
                </Field>

                <Field label={t('expires', 'Vence')}>
                    <span className={expired_at ? 'text-gray-800 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500'}>
                        {expired_at
                            ? dateFormat(cleanIsoDate(expired_at))
                            : t('without_date', 'Sin fecha')}
                    </span>
                </Field>

                <SectionDivider />

                <Field label={t('createdBy', 'Creado por')}>
                    <div className="flex items-center gap-2 min-w-0">
                        <AvatarProfile user={withBotAvatar(owner || USER_SYS)} size={20} />
                        <span className="text-[13px] text-gray-700 dark:text-gray-200 truncate">
                            {(owner || USER_SYS)?.name} {(owner || USER_SYS)?.lastname}
                        </span>
                    </div>
                </Field>

                <Field label={t('created', 'Creado')}>
                    <span className="text-gray-600 dark:text-gray-400">
                        {created_at
                            ? dateFormat(cleanIsoDate(created_at), 'DD-MM-YYYY HH:mm')
                            : '-'}
                    </span>
                </Field>

                {ticket_category_primary?.parent?.label?.es && (
                    <Field label={t('category', 'Categoría padre')}>
                        <span className="text-gray-700 dark:text-gray-200 truncate block">
                            {ticket_category_primary.parent.label.es}
                        </span>
                    </Field>
                )}

                {external_code && (
                    <Field label={t('external_code', 'Código externo')}>
                        <span className="font-mono text-[12px] text-gray-600 dark:text-gray-300">{external_code}</span>
                    </Field>
                )}

            </div>
        </AsideCard>
    )
}

export default TicketDetailsCard
